
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { buildEtsyOAuthUrl } from '@/utils/etsyAuth';
import { useSubscriptionStatus } from '@/hooks/useSubscriptionStatus';
import { clearCache } from '@/utils/etsyCache';
import { getCachedUserData } from '@/utils/userCache';

export const useEtsyIntegration = () => {
  const [isConnecting, setIsConnecting] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const { toast } = useToast();
  const { hasActiveSubscription } = useSubscriptionStatus();

  const connectEtsy = async () => {
    if (!hasActiveSubscription) {
      toast({
        title: "Subscription Required",
        description: "You need an active subscription to connect to Etsy",
        variant: "destructive",
      });
      return;
    }

    setIsConnecting(true);
    try {
      const authUrl = await buildEtsyOAuthUrl();
      window.location.href = authUrl;
    } catch (error: any) {
      console.error('Etsy connection error:', error);
      toast({
        title: "Connection Failed",
        description: error.message || "Failed to connect to Etsy",
        variant: "destructive",
      });
      setIsConnecting(false);
    }
  };

  const disconnectEtsy = async () => {
    setIsDisconnecting(true);
    try {
      const user = await getCachedUserData();
      if (!user) throw new Error('User not authenticated');

      // Remove the Etsy shop connection from the database
      const { error } = await supabase
        .from('etsy_shops')
        .delete()
        .eq('user_id', user.id);

      if (error) throw error;

      // Clear cached orders and localStorage data
      clearCache();

      toast({
        title: "Disconnected",
        description: "Successfully disconnected from Etsy",
      });

    } catch (error: any) {
      console.error('Error disconnecting from Etsy:', error);
      toast({
        title: "Disconnect Failed",
        description: error.message || "Failed to disconnect from Etsy",
        variant: "destructive",
      });
    } finally {
      setIsDisconnecting(false);
    }
  };

  const checkEtsyConnection = async () => {
    if (!hasActiveSubscription) {
      return false;
    }

    try {
      const user = await getCachedUserData();
      if (!user) {
        return false;
      }

      const { data: shops } = await supabase
        .from('etsy_shops')
        .select('shop_name')
        .eq('user_id', user.id)
        .single();

      const isConnected = !!shops;
      
      console.log('Fetched Etsy connection status:', isConnected);
      return isConnected;
    } catch {
      return false;
    }
  };

  return {
    connectEtsy,
    disconnectEtsy,
    checkEtsyConnection,
    isConnecting,
    isDisconnecting,
  };
};
